import { GraphColorsInOrder, graphSettings, OptoColors } from '@/config/settings';

/**
 * The CSS custom properties a canvas chart reads, without the leading `--`.
 *
 * The chart and data tokens live in styles/tokens/_chart.scss. The four primitives at the end
 * are fills that keep one value in both themes; they are listed so charts that paint them
 * read them through the same bridge instead of their own `getComputedStyle` call.
 */
export const CHART_TOKEN_NAMES = [
    'chart-text',
    'chart-axis',
    'chart-grid',
    'chart-surface',
    'chart-label-ink',
    'chart-tooltip-bg',
    'chart-tooltip-text',
    'chart-tooltip-border',
    'chart-zoom-fill',
    'chart-zoom-border',
    'chart-bar-ink-dark',
    'chart-bar-ink-light',
    'series-1',
    'series-2',
    'series-3',
    'series-4',
    'series-5',
    'series-6',
    'series-7',
    'series-8',
    'series-9',
    'series-10',
    'series-11',
    'series-12',
    'data-trout',
    'data-minmax-max',
    'data-weight-distribution',
    'data-band-fill',
    'data-temperature',
    'data-wound-temperature',
    'data-day-marker',
    'alert-warning-600',
    'alert-critical-400',
    'accent-ocean-green-400',
    'accent-orange-400',
] as const;

export type ChartTokenName = (typeof CHART_TOKEN_NAMES)[number];

export const SERIES_TOKEN_NAMES = [
    'series-1',
    'series-2',
    'series-3',
    'series-4',
    'series-5',
    'series-6',
    'series-7',
    'series-8',
    'series-9',
    'series-10',
    'series-11',
    'series-12',
] as const satisfies readonly ChartTokenName[];

const lower = (color: string): string => color.toLowerCase();

/**
 * The light value of every token, used where no stylesheet defines them (jsdom, a detached
 * document). The page itself always paints what the stylesheet says. chartTokens.test.ts
 * checks these against _chart.scss and css-variables.scss, so the two cannot drift.
 */
export const CHART_TOKEN_FALLBACKS: Readonly<Record<ChartTokenName, string>> = {
    'chart-text': '#5d6182',
    'chart-axis': '#a6a6a6',
    'chart-grid': '#e9ebf5',
    'chart-surface': '#ffffff',
    'chart-label-ink': '#14192d',
    'chart-tooltip-bg': '#14192d',
    'chart-tooltip-text': '#ffffff',
    'chart-tooltip-border': '#14192d',
    'chart-zoom-fill': '#e1e1e1',
    'chart-zoom-border': '#000000',
    'chart-bar-ink-dark': '#14192d',
    'chart-bar-ink-light': '#ffffff',
    'series-1': lower(GraphColorsInOrder[0]),
    'series-2': lower(GraphColorsInOrder[1]),
    'series-3': lower(GraphColorsInOrder[2]),
    'series-4': lower(GraphColorsInOrder[3]),
    'series-5': lower(GraphColorsInOrder[4]),
    'series-6': lower(GraphColorsInOrder[5]),
    'series-7': lower(GraphColorsInOrder[6]),
    'series-8': lower(GraphColorsInOrder[7]),
    'series-9': lower(GraphColorsInOrder[8]),
    'series-10': lower(GraphColorsInOrder[9]),
    'series-11': lower(GraphColorsInOrder[10]),
    'series-12': lower(GraphColorsInOrder[11]),
    'data-trout': '#274f8c',
    'data-minmax-max': lower(graphSettings.minMaxAvgColors.max),
    'data-weight-distribution': lower(graphSettings.graphColors.weightDistribution),
    'data-band-fill': lower(graphSettings.graphColors.band),
    'data-temperature': lower(graphSettings.graphColors.temperature),
    'data-wound-temperature': lower(graphSettings.graphColors.woundTemperature),
    'data-day-marker': lower(graphSettings.graphColors.dayMarker),
    'alert-warning-600': '#ffb81f',
    'alert-critical-400': '#e87166',
    'accent-ocean-green-400': '#5bb784',
    'accent-orange-400': '#f4a46f',
};

/**
 * Palette colours whose dark value differs, by the token that carries both values.
 *
 * The palettes in config/settings.ts and the route modules keep naming their light hex, and a
 * chart passes each colour through `ChartTheme.color`, which swaps in the token. Keyed by
 * lower-case hex. Only colours under 3:1 on the dark card, or pale enough to glare on it, are
 * here; every other palette colour reads well on both surfaces and is drawn as given.
 */
export const PALETTE_LIFT_TOKENS: Readonly<Partial<Record<string, ChartTokenName>>> = {
    [lower(OptoColors.Riverside)]: 'series-1',
    [lower(OptoColors.Red)]: 'series-3',
    [lower(OptoColors.Lavender)]: 'series-5',
    [lower(OptoColors.Purple)]: 'series-9',
    // graphSettings.fishTypeName.trout and rainbow_trout.
    '#274f8c': 'data-trout',
    [lower(graphSettings.minMaxAvgColors.max)]: 'data-minmax-max',
    // The weight distribution bars (cage and Smolt report).
    [lower(graphSettings.graphColors.weightDistribution)]: 'data-weight-distribution',
    // The pale band behind Behavior and Health series.
    [lower(graphSettings.graphColors.band)]: 'data-band-fill',
    // graphSettings.graphColors.temperature: its band, at the fade's top opacity, glared on
    // the dark card.
    [lower(graphSettings.graphColors.temperature)]: 'data-temperature',
    // The wound graphs' temperature line, drawn at about 49% alpha: about 2:1 on the dark card.
    [lower(graphSettings.graphColors.woundTemperature)]: 'data-wound-temperature',
    // The Harvest and Smolt report day lines, faint on the dark card.
    [lower(graphSettings.graphColors.dayMarker)]: 'data-day-marker',
    // The feeding target line is the chart's muted text colour, $neutral-700.
    '#5d6182': 'chart-text',
};

/** The app font, `$regular-font` in styles/variables.scss. */
export const CHART_FONT_FALLBACK = "'BoxedRoundBook', sans-serif";

/**
 * The face for tooltip titles and footers, which Chart.js sets in bold. Each BoxedRound face is
 * its own family with a single normal weight, and a canvas draws a bold request on one of them
 * no heavier, so the emphasis has to come from the heavier face (`$font-medium`).
 */
export const CHART_STRONG_FONT = "'BoxedRoundMedium', sans-serif";
